// Admin: Create / Edit a marketplace car listing.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Save, Upload, X, Star, StarOff } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, notifyErr } from '../../../components/admin/billing/shared';

const INITIAL = {
  make: '', model: '', variant: '',
  manufacture_year: new Date().getFullYear() - 2,
  registration_year: '',
  km_driven: 30000,
  fuel_type: 'petrol', transmission: 'manual',
  body_type: 'sedan', color: '', owners: 1,
  registration_state: '', registration_number: '',
  asking_price: 500000, listing_type: 'unisol_inventory',
  status: 'draft', city: 'Bengaluru',
  description: '', highlights: [],
  seller_name: 'Unisol Global', seller_phone: '', seller_email: '',
  photos: [], cover_photo: '',
  inspection_id: '',
};

const inputCls = 'w-full bg-white border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-teal-400';

const AdminMarketplaceForm = () => {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { authHeader } = useAuth();
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [highlightInput, setHighlightInput] = useState('');
  const [publicInspections, setPublicInspections] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/used-car-inspections`, {
          headers: authHeader(), params: { limit: 200 },
        });
        // Only offer inspections that have been made publicly shareable
        setPublicInspections((data || []).filter((d) => d.is_public && d.share_token));
      } catch {
        // Non-fatal — user might not have inspections permission
        setPublicInspections([]);
      }
    })();
  }, [authHeader]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    if (!isEdit) return;
    try {
      const { data } = await axios.get(`${API}/marketplace/admin/cars`, {
        headers: authHeader(),
        params: { limit: 200 },
      });
      const found = (data.items || []).find((c) => c.id === id);
      if (found) {
        // Normalise nulls (from the API) to '' so React inputs don't flip
        // between controlled/uncontrolled.
        const clean = Object.fromEntries(
          Object.entries(found).map(([k, v]) => [k, v == null ? '' : v]),
        );
        setForm({ ...INITIAL, ...clean });
      }
    } catch (e) {
      notifyErr(e);
    } finally {
      setLoading(false);
    }
  }, [isEdit, id, authHeader]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        manufacture_year: Number(form.manufacture_year),
        registration_year: form.registration_year ? Number(form.registration_year) : null,
        km_driven: Number(form.km_driven),
        owners: Number(form.owners),
        asking_price: Number(form.asking_price),
      };
      if (isEdit) {
        await axios.put(`${API}/marketplace/admin/cars/${id}`, payload, { headers: authHeader() });
      } else {
        const { data } = await axios.post(`${API}/marketplace/admin/cars`, payload, { headers: authHeader() });
        nav(`/admin/marketplace/${data.id}/edit`);
        return;
      }
      alert('Saved');
    } catch (err) {
      notifyErr(err);
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!isEdit) {
      alert('Save the car first, then upload photos.');
      return;
    }
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await axios.post(
        `${API}/marketplace/admin/cars/${id}/photos`, fd,
        { headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' } },
      );
      const url = data?.secure_url;
      if (url) {
        setForm((p) => ({
          ...p,
          photos: [...(p.photos || []), url],
          cover_photo: p.cover_photo || url,
        }));
      }
    } catch (err) {
      notifyErr(err);
    }
  };

  const removePhoto = async (url) => {
    if (!isEdit) return;
    if (!window.confirm('Remove this photo?')) return;
    try {
      await axios.delete(`${API}/marketplace/admin/cars/${id}/photos`, {
        headers: authHeader(), params: { url },
      });
      setForm((p) => ({
        ...p,
        photos: (p.photos || []).filter((x) => x !== url),
        cover_photo: p.cover_photo === url ? ((p.photos || []).find((x) => x !== url) || '') : p.cover_photo,
      }));
    } catch (err) {
      notifyErr(err);
    }
  };

  const makeCover = (url) => update('cover_photo', url);

  const addHighlight = () => {
    const v = highlightInput.trim();
    if (!v) return;
    update('highlights', [...(form.highlights || []), v]);
    setHighlightInput('');
  };
  const removeHighlight = (h) => update('highlights', (form.highlights || []).filter((x) => x !== h));

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-marketplace-form">
      <Link to="/admin/marketplace" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft size={14} /> Back to listings
      </Link>

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{isEdit ? 'Edit listing' : 'New car listing'}</h1>
        <button form="car-form" type="submit" disabled={saving}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-60 text-white font-semibold px-5 py-2.5 rounded-lg text-sm inline-flex items-center gap-2"
                data-testid="save-car-btn">
          <Save size={14} /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      <form id="car-form" onSubmit={submit} className="space-y-6">
        {/* Basic */}
        <Card title="Basic information">
          <Grid>
            <Field label="Make *">
              <input required value={form.make} onChange={(e) => update('make', e.target.value)} className={inputCls} placeholder="Honda" data-testid="f-make" />
            </Field>
            <Field label="Model *">
              <input required value={form.model} onChange={(e) => update('model', e.target.value)} className={inputCls} placeholder="City" data-testid="f-model" />
            </Field>
            <Field label="Variant">
              <input value={form.variant} onChange={(e) => update('variant', e.target.value)} className={inputCls} placeholder="VX MT" />
            </Field>
            <Field label="Manufacture year *">
              <input type="number" required value={form.manufacture_year} onChange={(e) => update('manufacture_year', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Registration year">
              <input type="number" value={form.registration_year} onChange={(e) => update('registration_year', e.target.value)} className={inputCls} />
            </Field>
            <Field label="KM driven *">
              <input type="number" required value={form.km_driven} onChange={(e) => update('km_driven', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Owners">
              <input type="number" min={1} value={form.owners} onChange={(e) => update('owners', e.target.value)} className={inputCls} />
            </Field>
            <Field label="Colour">
              <input value={form.color} onChange={(e) => update('color', e.target.value)} className={inputCls} placeholder="Pearl White" />
            </Field>
          </Grid>
        </Card>

        {/* Tech */}
        <Card title="Specifications">
          <Grid>
            <Field label="Fuel *">
              <select value={form.fuel_type} onChange={(e) => update('fuel_type', e.target.value)} className={inputCls}>
                {['petrol', 'diesel', 'cng', 'electric', 'hybrid'].map((o) => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Transmission *">
              <select value={form.transmission} onChange={(e) => update('transmission', e.target.value)} className={inputCls}>
                {['manual', 'automatic', 'amt', 'cvt', 'dct'].map((o) => <option key={o} value={o}>{o.toUpperCase()}</option>)}
              </select>
            </Field>
            <Field label="Body type">
              <select value={form.body_type} onChange={(e) => update('body_type', e.target.value)} className={inputCls}>
                <option value="">—</option>
                {['hatchback', 'sedan', 'suv', 'muv', 'coupe', 'convertible', 'pickup', 'van', 'wagon'].map((o) => (
                  <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>
                ))}
              </select>
            </Field>
            <Field label="RTO state">
              <input value={form.registration_state} onChange={(e) => update('registration_state', e.target.value)} className={inputCls} placeholder="KA" maxLength={3} />
            </Field>
            <Field label="Registration number">
              <input value={form.registration_number} onChange={(e) => update('registration_number', e.target.value)} className={inputCls} placeholder="KA01AB1234" />
            </Field>
            <Field label="City">
              <input value={form.city} onChange={(e) => update('city', e.target.value)} className={inputCls} placeholder="Bengaluru" />
            </Field>
          </Grid>
        </Card>

        {/* Pricing & status */}
        <Card title="Pricing & visibility">
          <Grid>
            <Field label="Asking price (₹) *">
              <input type="number" required value={form.asking_price} onChange={(e) => update('asking_price', e.target.value)} className={inputCls} data-testid="f-price" />
            </Field>
            <Field label="Listing type">
              <select value={form.listing_type} onChange={(e) => update('listing_type', e.target.value)} className={inputCls}>
                <option value="unisol_inventory">Unisol Inventory</option>
                <option value="dealer_partner">Dealer Partner</option>
              </select>
            </Field>
            <Field label="Status">
              <select value={form.status} onChange={(e) => update('status', e.target.value)} className={inputCls} data-testid="f-status">
                <option value="draft">Draft (hidden)</option>
                <option value="published">Published</option>
                <option value="sold">Sold</option>
                <option value="archived">Archived</option>
              </select>
            </Field>
          </Grid>
        </Card>

        {/* Seller */}
        <Card title="Seller contact (visible to buyers)">
          <Grid>
            <Field label="Seller name *">
              <input required value={form.seller_name} onChange={(e) => update('seller_name', e.target.value)} className={inputCls} data-testid="f-seller-name" />
            </Field>
            <Field label="Phone (for Call & WhatsApp) *">
              <input required value={form.seller_phone} onChange={(e) => update('seller_phone', e.target.value)} className={inputCls} placeholder="+91 98765 43210" data-testid="f-seller-phone" />
            </Field>
            <Field label="Email">
              <input type="email" value={form.seller_email} onChange={(e) => update('seller_email', e.target.value)} className={inputCls} />
            </Field>
          </Grid>
        </Card>

        {/* Attach Inspection Report */}
        <Card title="Attach Unisol inspection report (optional)">
          <p className="text-xs text-slate-500 mb-3">
            Link a completed <strong>Used Cars Inspection</strong> that has been made public.
            Buyers viewing this listing will see a <em>View Full Inspection Report</em> button.
          </p>
          <select
            value={form.inspection_id || ''}
            onChange={(e) => update('inspection_id', e.target.value)}
            className={inputCls}
            data-testid="f-inspection-id"
          >
            <option value="">— None —</option>
            {publicInspections.map((ins) => {
              const label = [ins.make, ins.model, ins.variant].filter(Boolean).join(' ');
              const suffix = ins.registration_number || ins.booking_id;
              return (
                <option key={ins.id} value={ins.id}>
                  {label}{suffix ? ` · ${suffix}` : ''}{ins.overall_grade ? ` · ${ins.overall_grade}` : ''}
                </option>
              );
            })}
          </select>
          {publicInspections.length === 0 && (
            <p className="text-xs text-amber-600 mt-2">
              No public inspections yet. Open <a href="/admin/used-car-inspections" className="underline">Used Cars Inspection</a> and click <em>Enable public link</em> on a completed record.
            </p>
          )}
        </Card>

        {/* Highlights */}
        <Card title="Key highlights (bullet list)">
          <div className="flex gap-2">
            <input value={highlightInput} onChange={(e) => setHighlightInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addHighlight(); } }}
              placeholder="e.g. Sunroof, ventilated seats, 6 airbags" className={inputCls + ' flex-1'} />
            <button type="button" onClick={addHighlight} className="bg-slate-900 text-white px-4 rounded-lg text-sm font-semibold">Add</button>
          </div>
          {form.highlights?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {form.highlights.map((h) => (
                <span key={h} className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-medium px-3 py-1 rounded-full">
                  {h}
                  <button type="button" onClick={() => removeHighlight(h)} className="text-teal-700 hover:text-rose-600"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* Description */}
        <Card title="Description">
          <textarea value={form.description} onChange={(e) => update('description', e.target.value)} rows={5}
            className={inputCls + ' resize-y'} placeholder="Single-owner, garage-kept, full service history at authorised dealer…" />
        </Card>

        {/* Photos */}
        <Card title="Photos">
          {!isEdit ? (
            <p className="text-sm text-slate-500">Save the car first — then upload photos here.</p>
          ) : (
            <>
              <label className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer" data-testid="upload-photo-btn">
                <Upload size={14} /> Upload photo
                <input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPhoto(f); e.target.value = ''; }} />
              </label>
              <p className="text-xs text-slate-500 mt-2">Click the star icon to pick the cover photo shown on the listings page.</p>
              {(form.photos || []).length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
                  {form.photos.map((p) => (
                    <div key={p} className={`relative group rounded-lg overflow-hidden border-2 ${form.cover_photo === p ? 'border-teal-500' : 'border-transparent'}`}>
                      <img src={p} alt={`${form.make} ${form.model}`} className="w-full h-32 object-cover bg-slate-100" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                        <button type="button" onClick={() => makeCover(p)} className="bg-white text-slate-900 p-2 rounded-full" title="Set as cover">
                          {form.cover_photo === p ? <Star size={14} className="text-amber-500 fill-amber-500" /> : <StarOff size={14} />}
                        </button>
                        <button type="button" onClick={() => removePhoto(p)} className="bg-rose-600 text-white p-2 rounded-full" title="Remove">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 mt-3">No photos yet.</p>
              )}
            </>
          )}
        </Card>
      </form>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5">
    <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">{title}</h2>
    {children}
  </div>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>;
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

export default AdminMarketplaceForm;
